/*
 * DomManager
 *
 * Manages DOM updates for image cropping, including preview images,
 * hidden fields, and file inputs. Provides a consistent interface for
 * different controller implementations.
 *
 * Usage:
 *   const manager = new DomManager(errorHandler)
 *   manager.updatePreview(previewElement, dataUrl)
 *   manager.setHiddenFieldValue(inputElement, value)
 *   manager.setFileInput(inputElement, file)
 */
export class DomManager {
  /*
   * constructor
   *
   * Creates a new DomManager instance.
   *
   * Input:
   *   errorHandler {ErrorHandler} - Error handler for reporting errors.
   */
  constructor(errorHandler) {
    this.errorHandler = errorHandler
    this._blobUrls = new Map()
  }

  /*
   * updatePreview
   *
   * Updates a preview image element with a new source and makes it visible.
   *
   * Inputs:
   *   element {HTMLImageElement} - The preview image element.
   *   src     {string}           - The new image source (data URL or blob URL).
   *
   * Output: none (side effect: updates element)
   */
  updatePreview(element, src) {
    if (!element) return
    element.src = src
    element.classList.remove("d-none")
  }

  /*
   * showElement
   *
   * Makes an element visible by removing the d-none class.
   *
   * Input:
   *   element {HTMLElement} - The element to show.
   *
   * Output: none (side effect: removes d-none class)
   */
  showElement(element) {
    if (element) element.classList.remove("d-none")
  }

  /*
   * hideElement
   *
   * Hides an element by adding the d-none class.
   *
   * Input:
   *   element {HTMLElement} - The element to hide.
   *
   * Output: none (side effect: adds d-none class)
   */
  hideElement(element) {
    if (element) element.classList.add("d-none")
  }

  /*
   * setHiddenFieldValue
   *
   * Sets the value of a hidden input field.
   *
   * Inputs:
   *   element {HTMLInputElement} - The hidden input element.
   *   value   {string}           - The value to set.
   *
   * Output: none (side effect: updates element value)
   */
  setHiddenFieldValue(element, value) {
    if (element) element.value = value
  }

  /*
   * getHiddenFieldValue
   *
   * Gets the value of a hidden input field.
   *
   * Input:
   *   element {HTMLInputElement} - The hidden input element.
   *
   * Output:
   *   {string} - The field value, or empty string if element is null.
   */
  getHiddenFieldValue(element) {
    return element?.value || ""
  }

  /*
   * setFileInput
   *
   * Sets a file input's files property using DataTransfer.
   *
   * Inputs:
   *   element {HTMLInputElement} - The file input element.
   *   file    {File}             - The file to set.
   *
   * Output: none (side effect: updates element files)
   */
  setFileInput(element, file) {
    if (!element || !file) return
    const dt = new DataTransfer()
    dt.items.add(file)
    element.files = dt.files
  }

  /*
   * hasFiles
   *
   * Checks if a file input has any files selected.
   *
   * Input:
   *   element {HTMLInputElement} - The file input element.
   *
   * Output:
   *   {boolean} - True if the input has files.
   */
  hasFiles(element) {
    return element && element.files && element.files.length > 0
  }

  /*
   * storeBlobUrl
   *
   * Stores a blob URL for later cleanup, revoking any previous URL
   * stored with the same key.
   *
   * Inputs:
   *   key     {string} - Unique key for this blob URL.
   *   blobUrl {string} - The blob URL to store.
   *
   * Output: none (side effect: stores URL, revokes previous)
   */
  storeBlobUrl(key, blobUrl) {
    const previous = this._blobUrls.get(key)
    if (previous) {
      URL.revokeObjectURL(previous)
    }
    this._blobUrls.set(key, blobUrl)
  }

  /*
   * getBlobUrl
   *
   * Retrieves a stored blob URL by key.
   *
   * Input:
   *   key {string} - The key for the blob URL.
   *
   * Output:
   *   {string|undefined} - The blob URL, or undefined if not found.
   */
  getBlobUrl(key) {
    return this._blobUrls.get(key)
  }

  /*
   * revokeAllBlobUrls
   *
   * Revokes all stored blob URLs to free memory.
   *
   * Output: none (side effect: revokes URLs, clears storage)
   */
  revokeAllBlobUrls() {
    this._blobUrls.forEach((url) => URL.revokeObjectURL(url))
    this._blobUrls.clear()
  }

  /*
   * revokeBlobUrl
   *
   * Revokes a specific stored blob URL.
   *
   * Input:
   *   key {string} - The key for the blob URL to revoke.
   *
   * Output: none (side effect: revokes URL, removes from storage)
   */
  revokeBlobUrl(key) {
    const url = this._blobUrls.get(key)
    if (url) {
      URL.revokeObjectURL(url)
      this._blobUrls.delete(key)
    }
  }

  /*
   * parseJsonField
   *
   * Parses JSON from a hidden input field, returning null on failure.
   *
   * Input:
   *   element {HTMLInputElement} - The hidden input containing JSON.
   *
   * Output:
   *   {object|null} - Parsed object, or null if empty/invalid.
   */
  parseJsonField(element) {
    const value = this.getHiddenFieldValue(element)
    if (!value || value === "null") return null
    try {
      return JSON.parse(value)
    } catch {
      return null
    }
  }

  /*
   * setJsonField
   *
   * Serialises an object to JSON and stores it in a hidden input.
   *
   * Inputs:
   *   element {HTMLInputElement} - The hidden input element.
   *   data    {object|null}      - The data to serialise.
   *
   * Output: none (side effect: updates element value)
   */
  setJsonField(element, data) {
    if (element) {
      element.value = data ? JSON.stringify(data) : ""
    }
  }

  /*
   * getDataAttribute
   *
   * Gets a data attribute value from an element.
   *
   * Inputs:
   *   element {HTMLElement} - The element to read from.
   *   name    {string}      - The data attribute name (without "data-" prefix).
   *
   * Output:
   *   {string} - The attribute value, or empty string if not found.
   */
  getDataAttribute(element, name) {
    return element?.dataset?.[name] || ""
  }
}
